// DatabaseSettingsMissionsTab.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import DatabaseSettingsMissionTemplateRow from './DatabaseSettingsMissionTemplateRow';
import DatabaseSettingsAddButton from 'collection/settings/addbutton/DatabaseSettingsAddButton';

/**
 * DatabaseSettingsMissionsTab
 *
 * Renders the Workflow template list inside database settings.
 *
 * @param {boolean} isSaving - Whether task persistence is in flight.
 * @param {function} onAddMissionTemplate - Creates a workflow and opens the designer.
 * @param {function} onDesignMissionTemplate - Opens the workflow designer.
 * @param {function} onReorderMissionTemplate - Persists a reordered Workflow position.
 *
 * @returns {ReactNode} The rendered Workflows tab.
 */
const DatabaseSettingsMissionsTab = React.memo(({
  isSaving = false,
  missionTemplates = [],
  onAddMissionTemplate,
  onDesignMissionTemplate,
  onReorderMissionTemplate,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [orderedTemplates, setOrderedTemplates] = useState([]);
  const [draggingTemplateKey, setDraggingTemplateKey] = useState('');
  const [dragOverTemplateKey, setDragOverTemplateKey] = useState('');

  // ----- MEMOS ----- //

  const missionTemplateRows = useMemo(() => {

    return (Array.isArray(missionTemplates) ? missionTemplates : []).map((template) => {
      const taskCount = Array.isArray(template?.tasks) ? template.tasks.length : 0;

      return {
        key: String(template?.key || '').trim(),
        iconKey: String(template?.iconKey || '').trim() || 'account_tree',
        label: String(template?.title || '').trim() || 'Untitled Playbook',
        note: `${taskCount} task${taskCount === 1 ? '' : 's'}`,
        template,
      };
    }).filter((template) => template.key);
  }, [missionTemplates]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setOrderedTemplates(missionTemplateRows);
  }, [missionTemplateRows]);

  // ----- HANDLERS ----- //

  const handleDragStart = useCallback((template, event) => {
    const nextTemplateKey = String(template?.key || '').trim();
    if (!nextTemplateKey) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', nextTemplateKey);
    setDraggingTemplateKey(nextTemplateKey);
  }, []);

  const handleDragOver = useCallback((template, event) => {
    const nextTemplateKey = String(template?.key || '').trim();
    if (!draggingTemplateKey || !nextTemplateKey || draggingTemplateKey === nextTemplateKey) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverTemplateKey(nextTemplateKey);
  }, [draggingTemplateKey]);

  const handleDragEnd = useCallback(() => {
    setDraggingTemplateKey('');
    setDragOverTemplateKey('');
  }, []);

  const handleDrop = useCallback(async (template, event) => {
    const targetTemplateKey = String(template?.key || '').trim();
    if (!draggingTemplateKey || !targetTemplateKey || draggingTemplateKey === targetTemplateKey) {
      handleDragEnd();
      return;
    }

    event.preventDefault();

    const previousTemplates = orderedTemplates;
    const draggedIndex = previousTemplates.findIndex((row) => row?.key === draggingTemplateKey);
    const targetIndex = previousTemplates.findIndex((row) => row?.key === targetTemplateKey);
    if (draggedIndex < 0 || targetIndex < 0) {
      handleDragEnd();
      return;
    }

    const nextTemplates = [...previousTemplates];
    const [movedTemplate] = nextTemplates.splice(draggedIndex, 1);
    nextTemplates.splice(targetIndex, 0, movedTemplate);

    setOrderedTemplates(nextTemplates);
    handleDragEnd();

    try {
      await onReorderMissionTemplate?.(draggingTemplateKey, targetTemplateKey);
    } catch {
      setOrderedTemplates(previousTemplates);
    }
  }, [draggingTemplateKey, handleDragEnd, onReorderMissionTemplate, orderedTemplates]);

  // ----- RENDER ----- //

  return (
    <div className="database-modal-views">
      <DatabaseSettingsAddButton
        title="Add Playbook"
        onClick={() => onAddMissionTemplate?.()}
      />

      {orderedTemplates.length > 0 ? (
        <div className="database-modal-view-list">
          {orderedTemplates.map((template) => (
            <DatabaseSettingsMissionTemplateRow
              key={template.key}
              iconKey={template.iconKey}
              isDragging={draggingTemplateKey === template.key}
              isDropTarget={dragOverTemplateKey === template.key}
              label={template.label}
              note={template.note}
              onClick={() => onDesignMissionTemplate?.(template.template)}
              onDragEnd={handleDragEnd}
              onDragOver={(event) => handleDragOver(template.template, event)}
              onDragStart={(event) => handleDragStart(template.template, event)}
              onDrop={(event) => handleDrop(template.template, event)}
              onSettingsClick={() => onDesignMissionTemplate?.(template.template)}
            />
          ))}
        </div>
      ) : (
        <div
          className="database-modal-empty-state"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          No playbooks yet.
        </div>
      )}

      {isSaving ? (
        <div
          className="database-modal-empty-state"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          Saving playbook changes...
        </div>
      ) : null}
    </div>
  );
});

// Dev Tools
DatabaseSettingsMissionsTab.displayName = 'DatabaseSettingsMissionsTab';

export default DatabaseSettingsMissionsTab;
